import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft } from "lucide-react";
import { useSeo, JsonLd, SITE_URL } from "@/lib/seo";
import { Footer } from "@/components/landing/Footer";
import { Wordmark } from "@/components/landing/Wordmark";
import { LiveFlowCells } from "@/components/home/LiveFlowCells";
import { FLOW_CELLS_URL, FLOW_CELLS_PAGE } from "@/components/home/FlowCellsBand";

const LBL = "font-mono text-[11px] uppercase tracking-[0.22em] text-[#7D8390]";

const READ = [
  ["SIZE", "Cell size = volume", "Bigger cells traded more. Spot where the market's attention and liquidity actually are."],
  ["GLOW", "Glow = how hard it moved", "Brighter cells moved further. Green glow is up, red is down, so outliers jump out instantly."],
  ["LEAN", "Bar = whale lean", "The bar inside each cell shows whether tracked Hyperliquid whale wallets lean long or short on that coin."],
  ["FLOW", "Light = whale rotation", "Light travelling between cells is whale money moving out of one coin and into another."],
];

const USES = [
  ["Catch rotations early", "See whales leave majors for alts (or flee to BTC) before it shows on a single chart."],
  ["Find the coin that matters today", "Volume and move strength together show which markets are in play right now."],
  ["Check your bias against whales", "Before you long a coin, see whether tracked wallets are leaning the same way or fading it."],
];

const FAQ = [
  { q: "What is Flow Cells?", a: "Flow Cells is a free live crypto market map from TradrQuant. Every coin is a cell: its size is trading volume, its glow is how hard it moved, and a bar inside shows which way tracked Hyperliquid whale wallets lean. Light between cells shows whale money rotating from one coin to another." },
  { q: "Is Flow Cells free?", a: "Yes. Flow Cells is free to explore in the TradrQuant terminal, with no card required." },
  { q: "Where does the whale data come from?", a: "Whale lean and rotation come from tracked wallets on Hyperliquid, whose on-chain order book makes positions public. Market data comes from live exchange feeds." },
  { q: "How is Flow Cells different from a normal crypto heatmap?", a: "A normal heatmap only shows price change. Flow Cells adds volume, the direction large wallets are positioned, and live rotation of whale capital between coins." },
  { q: "How often does Flow Cells update?", a: "It updates continuously in real time, with market and tracked-wallet data refreshed automatically." },
  { q: "Is this financial advice?", a: "No. Flow Cells is market data and analytics. It does not guarantee profits, and all trading carries risk." },
];

const ld = [
  { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
  { "@context": "https://schema.org", "@type": "SoftwareApplication", name: "TradrQuant Flow Cells", applicationCategory: "FinanceApplication", operatingSystem: "Web", url: FLOW_CELLS_URL, description: "Free live crypto heatmap of whale rotation: cell size is volume, glow is move strength, and a bar shows tracked Hyperliquid whale lean.", offers: { "@type": "Offer", price: "0", priceCurrency: "USD" } },
  { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [{ "@type": "ListItem", position: 1, name: "Home", item: SITE_URL }, { "@type": "ListItem", position: 2, name: "Flow Cells", item: `${SITE_URL}${FLOW_CELLS_PAGE}` }] },
];

const Cta = ({ tid }) => (
  <a href={FLOW_CELLS_URL} data-testid={tid} className="group inline-flex items-center gap-2 bg-[#BACD1C] px-6 py-3.5 font-mono text-[12px] uppercase tracking-[0.14em] text-black transition-colors duration-300 hover:bg-[#ECEBE4]">
    Open Flow Cells free <ArrowUpRight size={16} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
  </a>
);

export default function FlowCells() {
  useSeo({
    title: "Flow Cells — Free Live Crypto Whale Rotation Heatmap | TradrQuant",
    description: "Flow Cells is a free live crypto heatmap: every coin is a cell sized by volume, glowing by move strength, with tracked Hyperliquid whale lean and real-time whale money rotation between coins.",
    path: FLOW_CELLS_PAGE,
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="flow-cells-page" className="grain relative min-h-screen overflow-x-hidden bg-[#0A0A0A] text-white">
      <JsonLd data={ld} />
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1200px] items-center justify-between px-5 md:px-10">
          <Link to="/" aria-label="TradrQuant home"><Wordmark /></Link>
          <Link to="/" data-testid="flow-cells-back-btn" className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 transition-colors duration-300 hover:border-white/40 hover:text-white">
            <ChevronLeft size={14} /> cd ~/home
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-[1200px] px-5 md:px-10">
        <section className="py-16 md:py-24" data-testid="flow-cells-hero">
          <div>
            <p className={LBL}><span className="text-[#FFB224]">New</span> · free crypto whale heatmap</p>
            <h1 className="mt-5 max-w-4xl font-display text-[2.1rem] font-semibold leading-[1.05] tracking-[-0.035em] text-[#ECEBE4] sm:text-5xl">
              Flow Cells: every coin, every whale. <span className="text-[#7D8390]">One living map.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#9DA3AE] md:text-lg">
              A live crypto market heatmap that shows where volume is, which coins are moving hardest, which way tracked Hyperliquid whales lean, and how whale money rotates between coins in real time.
            </p>
            <div className="mt-8"><Cta tid="flow-cells-hero-cta" /></div>
          </div>
          <div className="mt-12"><LiveFlowCells /></div>
        </section>

        <section className="border-t border-white/[0.07] py-16" data-testid="flow-cells-read">
          <h2 className={LBL}>How to read Flow Cells</h2>
          <div className="mt-8 grid gap-px bg-white/[0.06] sm:grid-cols-2 lg:grid-cols-4">
            {READ.map(([k, t, d]) => (
              <article key={k} className="bg-[#0A0A0A] p-6">
                <p className="font-mono text-[11px] text-[#FFB224]">{k}</p>
                <h3 className="mt-2 font-display text-lg font-medium text-[#ECEBE4]">{t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#9DA3AE]">{d}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="border-t border-white/[0.07] py-16" data-testid="flow-cells-uses">
          <h2 className={LBL}>How traders use it</h2>
          <div className="mt-8 grid gap-8 md:grid-cols-3">
            {USES.map(([t, d]) => (
              <div key={t} className="border-l border-[#BACD1C]/40 pl-4">
                <h3 className="font-display text-lg font-medium text-[#ECEBE4]">{t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#9DA3AE]">{d}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="border-t border-white/[0.07] py-16" data-testid="flow-cells-faq">
          <h2 className={LBL}>FAQ</h2>
          <div className="mt-8 divide-y divide-white/[0.07]">
            {FAQ.map((f, i) => (
              <details key={f.q} data-testid={`flow-cells-faq-${i}`} className="group py-5">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-6 font-display text-lg text-[#ECEBE4]">
                  {f.q}<span className="font-mono text-[#7D8390] transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 max-w-3xl leading-relaxed text-[#9DA3AE]">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="flex flex-col items-start gap-6 border-t border-white/[0.07] py-16 md:flex-row md:items-center md:justify-between">
          <p className="max-w-xl font-display text-2xl text-[#ECEBE4]">Watch whale money rotate, live and free.</p>
          <Cta tid="flow-cells-bottom-cta" />
        </section>
      </main>
      <Footer />
    </div>
  );
}
